/**
 * History sync, over the native bridge into the player's own iCloud.
 *
 * The merge lives here rather than in Swift because the rule is a property of
 * the data structure, and the data structure is defined in JS. Native is dumb
 * storage: it holds opaque strings and says when they changed.
 *
 * The reason this is simple at all is the shape of the history. It is a map of
 * gameCode to grow-only sets of timestamps, so merging is union-and-dedupe per
 * key - commutative, associative and idempotent. Two devices that were both
 * offline for a week converge to the same answer whichever order they sync in,
 * with no last-write-wins and nothing lost. Timestamps are only ever compared
 * for equality, never ordered against each other, so clock skew between devices
 * cannot corrupt anything either.
 *
 * Streaks fall out of that for free - see streaks.js, which derives rather than
 * stores. A stored `{ current: 5 }` counter would have needed real conflict
 * resolution here.
 *
 * The one thing this cannot express is deletion: a removed entry comes back
 * from any device that still has it. Nothing calls GameStats.resetAll(), which
 * is what keeps that theoretical. If history ever needs trimming, trim by a
 * deterministic rule every device computes the same way rather than by picking
 * entries, or this needs tombstones.
 */
import GameStats, { STATS_EVENT } from './GameStats.js';
import {
  HISTORY_EVENT, isHistorySyncAvailable, requestHistoryPull, pushHistoryShards,
} from './bridge.js';

/** The three timestamp sets on every entry */
const EVENT_KEYS = ['attempts', 'completes', 'clears'];

/**
 * Split across keys rather than stored as one blob: iCloud's key-value store
 * caps a single value, and a cleared board should rewrite a few KB rather than
 * the entire history. 32 keys leaves plenty of room under the 1024 limit.
 */
export const SHARD_COUNT = 32;

/** iCloud gives an app 1 MB in total. Warn before writes start failing quietly. */
const BUDGET_BYTES = 900 * 1024;

/** Long enough to collect a burst of moves, short enough to survive a swipe-up */
const PUSH_DELAY_MS = 3000;

const asArray = (value) => (Array.isArray(value) ? value : []);

/**
 * Union two histories.
 *
 * `added` and `missing` are what the caller acts on: added means iCloud knew
 * something we did not, so save and tell the views; missing means we know
 * something iCloud does not, so push. Both can be true at once, and the launch
 * of a device that has been playing offline is exactly that case.
 *
 * @param {Object} base - ours
 * @param {Object} incoming - theirs
 * @returns {{merged: Object, added: number, missing: number}}
 */
export function mergeHistories(base = {}, incoming = {}) {
  const merged = {};
  let added = 0;
  let missing = 0;

  const codes = new Set([...Object.keys(base), ...Object.keys(incoming)]);

  codes.forEach((code) => {
    const mine = base[code] || {};
    const theirs = incoming[code] || {};
    // Spread first so anything a newer version of the app stores on an entry
    // survives a round trip through an older one
    const entry = { ...mine, ...theirs };

    EVENT_KEYS.forEach((key) => {
      const ours = new Set(asArray(mine[key]));
      const yours = new Set(asArray(theirs[key]));

      yours.forEach((timestamp) => { if (!ours.has(timestamp)) added += 1; });
      ours.forEach((timestamp) => { if (!yours.has(timestamp)) missing += 1; });

      entry[key] = [...new Set([...ours, ...yours])].sort((a, b) => a - b);
    });

    // Sticky, so a board first met as the lesson stays out of stats everywhere
    if (mine.isTutorial || theirs.isTutorial) entry.isTutorial = true;

    merged[code] = entry;
  });

  return { merged, added, missing };
}

/**
 * Which shard a board belongs in. djb2 - it only has to spread evenly and be
 * identical on every device forever, so it must never be "improved".
 * @param {string} gameCode
 * @returns {string}
 */
export function shardFor(gameCode) {
  let hash = 5381;
  for (let index = 0; index < gameCode.length; index += 1) {
    hash = ((hash << 5) + hash + gameCode.charCodeAt(index)) >>> 0;
  }
  return `h${hash % SHARD_COUNT}`;
}

/**
 * @param {Object} history
 * @returns {Object} - shard key to JSON string. Empty shards are omitted.
 */
export function toShards(history) {
  const buckets = {};
  Object.entries(history).forEach(([code, entry]) => {
    const key = shardFor(code);
    if (!buckets[key]) buckets[key] = {};
    buckets[key][code] = entry;
  });
  return Object.fromEntries(
    Object.entries(buckets).map(([key, bucket]) => [key, JSON.stringify(bucket)])
  );
}

/**
 * A board lives in exactly one shard, so reassembly cannot collide.
 * @param {Object} shards - values may be JSON strings or already-parsed objects
 * @returns {Object}
 */
export function fromShards(shards) {
  const history = {};
  Object.entries(shards || {}).forEach(([key, value]) => {
    try {
      const bucket = typeof value === 'string' ? JSON.parse(value) : value;
      if (bucket && typeof bucket === 'object') Object.assign(history, bucket);
    } catch (error) {
      // One unreadable shard costs those boards, not the whole history
      console.error(`Error reading synced history shard ${key}:`, error);
    }
  });
  return history;
}

/** Native may hand the map over as a JSON string or as an object */
const parsePayload = (payload) => {
  if (!payload) return {};
  if (typeof payload !== 'string') return payload;
  try {
    return JSON.parse(payload) || {};
  } catch (error) {
    console.error('Error parsing synced history payload:', error);
    return {};
  }
};

let pushTimer = null;

function pushNow() {
  if (pushTimer) {
    clearTimeout(pushTimer);
    pushTimer = null;
  }
  const shards = toShards(GameStats.getAllHistory());
  const bytes = Object.values(shards).reduce((total, json) => total + json.length, 0);
  if (bytes > BUDGET_BYTES) {
    console.warn(`History is ${bytes} bytes, near iCloud's 1 MB limit - writes may be dropped`);
  }
  pushHistoryShards(shards);
}

/** Throttled, not debounced: a run of quick clears should still reach iCloud */
function schedulePush() {
  if (pushTimer) return;
  pushTimer = setTimeout(pushNow, PUSH_DELAY_MS);
}

/**
 * @param {Object} incoming
 * @returns {{added: number, missing: number}}
 */
export function applyIncoming(incoming) {
  const { merged, added, missing } = mergeHistories(GameStats.getAllHistory(), incoming);

  if (added > 0) {
    GameStats.saveHistory(merged);
    // Streaks, history and stats are all derived - they need telling
    window.dispatchEvent(new CustomEvent(STATS_EVENT));
  }

  return { added, missing };
}

/**
 * Start syncing, if there is anything to sync with. Safe to call anywhere -
 * outside the app it does nothing at all.
 * @returns {boolean} - whether sync is running
 */
export function startHistorySync() {
  if (!isHistorySyncAvailable()) return false;

  window.addEventListener(HISTORY_EVENT, (event) => {
    const { missing } = applyIncoming(fromShards(parsePayload(event.detail)));
    // We hold boards iCloud has never seen. Without this a device whose history
    // is a superset of the store would merge, find nothing new, and never
    // volunteer its own - and the other device would never see those boards.
    if (missing > 0) schedulePush();
  });

  // Every recorded event, including one that arrived from the other device -
  // which is how the store ends up holding the union rather than whoever
  // wrote last. Merges that add nothing raise no event, so this cannot loop.
  window.addEventListener(STATS_EVENT, schedulePush);

  // Backgrounding is where a pending push would otherwise be lost
  const flush = () => { if (pushTimer) pushNow(); };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);

  requestHistoryPull();
  return true;
}
